<script lang="ts" setup>
import { useOverlay } from '@nuxt/ui/composables';
import { computed, onErrorCaptured, ref } from 'vue';
import { isBlockNotFoundError } from '#layers/block-engine/utils/block/errors';
import { useTranslationUtils } from '#layers/i18n/public';
import { ErrorDetailDialog } from '#layers/ui/public';

defineProps<{
	name?: string;
	icon?: string;
}>();

const error = ref<any>(null);

const isMissingBlockError = computed(() => isBlockNotFoundError(error.value));

const { translateString } = useTranslationUtils();
const overlay = useOverlay();

onErrorCaptured((err) => {
	console.error('BlockRender error:', err);
	error.value = err;
	return false;
});

function retry() {
	error.value = null;
}

function showDetails() {
	overlay.create(ErrorDetailDialog, {
		destroyOnClose: true,
		props: { error: error.value },
	}).open();
}
</script>

<template>
	<DNotice
		v-if="isMissingBlockError"
		color="warning"
		data-testid="block-error-state"
	>
		<template #title>
			<i18n-t keypath="block_render.missing" tag="span" scope="global">
				<template #block>
					<span class="font-mono">{{ translateString(name) }}</span>
				</template>
			</i18n-t>
		</template>
		<template #description>
			{{ $t('block_render.description') }}
		</template>
	</DNotice>
	<DAlert
		v-else-if="error"
		icon="lucide:circle-alert"
		color="error"
		:title="$t('block_render.error', { block: translateString(name) })"
		:description="error.message"
		data-testid="block-uncaught-error"
	>
		<template #actions>
			<DButton
				color="error"
				size="xs"
				:label="$t('block_render.retry')"
				data-testid="block-error-retry"
				@click="retry"
			/>
			<DButton
				variant="ghost"
				color="neutral"
				size="xs"
				:label="$t('common.view_details')"
				data-testid="block-error-details"
				@click="showDetails"
			/>
		</template>
	</DAlert>
	<slot v-else />
</template>
